import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'

import {
  ExtendedUnitFilterOptions,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import usePropertyControls from '@components/properties-view/v2/utils/use-property-controls'
import { PannellumDataInterface } from '@components/showcase-pannellum/types'

import FloorplateGallery from '@pages/properties-view/apartments/v2/floorplate-view/floorplate-gallery'

import { BuildingInterface } from '@api/building'
import {
  MappingBlockCollection,
  MappingCollection,
} from '@api/interactive-plan'

import useCheckMountedStatus from '@utilities/check-mounted-status'
import { getTabIndexByKey, getTabKeyByIndexBy } from '@utilities/floor-tab'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { PlayerState } from '@adUtilities/constants/common'

import GeneralExternalLinkFrame from '../../../common/GeneralExternalLinkFrame'
import PropertiesFooterButtons from '../../../common/PropertiesFooterButtons'
import useGetFloorPlateGalleries from '../../../hooks/useGetFloorPlateGalleries'
import useGetGeneralExternalLinks from '../../../hooks/useGetGeneralExternalLinks'
import Controls from './controls'
import FloorplanCanvas from './floorplan-canvas'

interface FloorPlateProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  firebaseActiveBlock: string
  firebaseActiveLevel: string
  building: BuildingInterface
  floorplan: MappingCollection
  blocks: MappingBlockCollection
  unitFilter: ExtendedUnitFilterOptions
  panoramic: PannellumDataInterface[]
  fullScreenToggle: boolean
  getVideoPlayerState: (arg: boolean) => void
  setFullScreenToggle: (arg: boolean) => void
}

function FloorplateView({
  projectIdentity,
  session,
  firebaseActiveBlock,
  firebaseActiveLevel,
  building,
  floorplan,
  blocks,
  unitFilter,
  panoramic,
  fullScreenToggle,
  getVideoPlayerState,
  setFullScreenToggle,
}: FloorPlateProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const { slides: floorGalleryItems } = useGetFloorPlateGalleries()

  const blockKeys: string[] = Object.keys(blocks) || []

  const [activeTab, setActiveTab] = useState<string>('floor')
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false)
  const [isExternalLinkFrameOpen, setIsExternalLinkFrameOpen] = useState(false)

  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0)
  const [playerState, setPlayerState] = useState<PlayerState>(PlayerState.Stop)
  const pinnedGalleryItemIndex =
    session?.floorGallery?.galleryControlV2?.pinnedItemIndex ?? null

  const { generalExternalLink } = useGetGeneralExternalLinks()

  const hasInteractiveMap = (): boolean => {
    let mapName = firebaseActiveLevel

    if (blockKeys.length > 1) {
      mapName = firebaseActiveBlock
        ? `${firebaseActiveBlock}-${firebaseActiveLevel}`
        : firebaseActiveLevel
    }

    if (mapName in floorplan) {
      return floorplan[mapName]?.image !== ''
    }

    return false
  }

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      floorGallery: {
        galleryControlV2: { activeTabIndex },
      },
    } = session

    setActiveTab(getTabKeyByIndexBy(activeTabIndex))
  }, [session])

  const isMounted = useCheckMountedStatus({
    disable: !session?.connected,
  })

  const resetPin = React.useCallback(() => {
    firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.pinnedItemIndex`]: null,
    })
  }, [])

  React.useEffect(
    () => () => {
      resetPin()
    },
    []
  )

  React.useEffect(() => {
    if (!isMounted) {
      return
    }
    resetPin()
  }, [session?.building?.activeLevel])

  const propertyControls = usePropertyControls({
    fullscreenConfig: projectIdentity.fullScreen,
    isFullscreenActive: fullScreenToggle,
    disableToggle: isVideoPlaying || playerState !== PlayerState.Stop,
  })

  const {
    forcedFullscreenIsActive,
    showControlsInForcedFullscreen,
    reset: resetControls,
  } = propertyControls

  const showPropertyControls = React.useMemo(() => {
    if (activeTab !== 'gallery') {
      return false
    }
    if (forcedFullscreenIsActive) {
      return showControlsInForcedFullscreen
    }
    return true
  }, [activeTab, forcedFullscreenIsActive, showControlsInForcedFullscreen])

  const handleFloorGalleryTabClick = async (tabKey: string) => {
    resetControls()
    await firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.activeSlideIndex`]: 0,
      [`floorGallery.galleryControlV2.activeTabIndex`]:
        getTabIndexByKey(tabKey),
    })
  }

  const handleBackActionClickOnGalleryTab = React.useCallback(() => {
    firebaseControlQuery.update({
      [`floorGallery.galleryControlV2.pinnedItemIndex`]: null,
    })
    handleFloorGalleryTabClick('floor')
  }, [])

  if (!hasInteractiveMap())
    return (
      <div className="flex h-full w-full items-center justify-center bg-white text-4xl">
        No interactive floor plate found for this level.
      </div>
    )

  if (isExternalLinkFrameOpen && generalExternalLink) {
    return (
      <GeneralExternalLinkFrame
        onCloseGeneralExternalLinkFrame={() =>
          setIsExternalLinkFrameOpen(false)
        }
        generalExternalLink={generalExternalLink}
        isFullscreen={fullScreenToggle}
      />
    )
  }

  return (
    <div className="relative flex h-full">
      <div className="absolute left-6 top-4 z-10">
        <Controls
          fullScreenToggle={fullScreenToggle}
          setFullScreenToggle={setFullScreenToggle}
          isVideoPlaying={isVideoPlaying || playerState !== PlayerState.Stop}
          isPinned={pinnedGalleryItemIndex !== null}
          hidePropertyControls={!showPropertyControls}
          currentSlideIndex={currentSlideIndex}
          onBackActionOverride={
            activeTab === 'gallery'
              ? handleBackActionClickOnGalleryTab
              : undefined
          }
          forceShowBackAction={activeTab === 'gallery'}
        />
      </div>

      {activeTab === 'gallery' && floorGalleryItems.length > 0 && (
        <FloorplateGallery
          floorGalleryItems={floorGalleryItems}
          isVideoPlaying={isVideoPlaying}
          setIsVideoPlaying={setIsVideoPlaying}
          getVideoPlayerState={getVideoPlayerState}
          currentSlideIndex={currentSlideIndex}
          setCurrentSlideIndex={setCurrentSlideIndex}
          playerState={playerState}
          setPlayerState={setPlayerState}
          propertyControlsProps={propertyControls}
        />
      )}

      {activeTab === 'floor' && (
        <FloorplanCanvas
          firebaseActiveBlock={firebaseActiveBlock}
          firebaseActiveLevel={firebaseActiveLevel}
          building={building}
          floorplan={floorplan}
          unitFilter={unitFilter}
          fullScreenToggle={fullScreenToggle}
        />
      )}

      <PropertiesFooterButtons
        panoramic={panoramic}
        activeTab={activeTab}
        blockKeys={blockKeys}
        isVideoPlaying={isVideoPlaying}
        shouldShowGalleryTab={floorGalleryItems.length > 0}
        onTabChange={(tab: string) => {
          handleFloorGalleryTabClick(tab)
        }}
        hasGeneralExternalLink={Boolean(generalExternalLink)}
        onOpenGeneralExternalLinkFrame={() => setIsExternalLinkFrameOpen(true)}
      />
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    session,
  })
)(FloorplateView)
